import { useCallback, useEffect, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { Entity } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Check, Phase } from './types';

export const REVIEW_ENV_REPOSITORY_ANNOTATION = 'reviewenv.ds25.io/repository';

export const hasReviewEnvs = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[REVIEW_ENV_REPOSITORY_ANNOTATION]);

const REFRESH_MS = 30_000;

export const PHASE_COLOR: Record<
  Phase,
  'success' | 'warning' | 'error' | 'info' | 'default'
> = {
  ready: 'success',
  degraded: 'warning',
  progressing: 'info',
  failed: 'error',
  orphaned: 'default',
};

export function CheckIcon({ status }: { status: Check['status'] }) {
  if (status === 'ok') {
    return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 16 }} />;
  }
  if (status === 'failed') {
    return <ErrorIcon sx={{ color: '#f44336', fontSize: 16 }} />;
  }
  return <HourglassEmptyIcon sx={{ color: '#ff9800', fontSize: 16 }} />;
}

export function age(createdAt: string | undefined, now: number): string {
  if (!createdAt) return '';
  const minutes = Math.floor((now - Date.parse(createdAt)) / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function githubUrl(
  slug: string | undefined,
  repository: string,
  pr: number,
): string | undefined {
  const org = slug?.split('/')[0];
  return org ? `https://github.com/${org}/${repository}/pull/${pr}` : undefined;
}

/**
 * GET a review-envs backend path and re-fetch it every REFRESH_MS. A failing
 * refresh keeps the last good data visible and only sets `error`.
 */
export function usePolledReviewEnvs<T>(path: string) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<{
    data?: T;
    error?: Error;
    loading: boolean;
  }>({ loading: true });

  const load = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('review-envs');
      const res = await fetchApi.fetch(`${baseUrl}/${path}`);
      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      const data = (await res.json()) as T;
      setState({ data, loading: false });
    } catch (e) {
      setState(prev => ({ ...prev, error: e as Error, loading: false }));
    }
  }, [discoveryApi, fetchApi, path]);

  useEffect(() => {
    setState({ loading: true });
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  return state;
}
